import { api } from "../../api/apiSlice";

// Stock Management (LPO -> Stock-In/GRN -> Stock-Out -> Return -> Adjustment,
// plus Stock Balances/QR) - TexasAlarms.Api's src/Stock module, shipped and
// verified live 2026-08-10, had no frontend anywhere until this module.
// Every list endpoint returns the shared PagedResult<T> shape
// ({items, totalCount, page, pageSize}) - callers read .items directly.
export const stockApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ---- LPO ----
    listLpos: builder.query({
      query: (params) => ({ url: "/stock/lpos", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((l) => ({ type: "StockLpo", id: l.recordGuid })),
              { type: "StockLpo", id: "LIST" },
            ]
          : [{ type: "StockLpo", id: "LIST" }],
    }),
    getLpo: builder.query({
      query: (recordGuid) => ({ url: `/stock/lpos/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "StockLpo", id: recordGuid }],
    }),
    getLpoRevisions: builder.query({
      query: (recordGuid) => ({ url: `/stock/lpos/${recordGuid}/revisions` }),
      providesTags: (result, error, recordGuid) => [{ type: "StockLpoRevisions", id: recordGuid }],
    }),
    createLpo: builder.mutation({
      query: (body) => ({ url: "/stock/lpos", method: "POST", data: body }),
      invalidatesTags: [{ type: "StockLpo", id: "LIST" }],
    }),
    updateLpo: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/stock/lpos/${recordGuid}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockLpo", id: recordGuid },
        { type: "StockLpo", id: "LIST" },
        { type: "StockLpoRevisions", id: recordGuid },
      ],
    }),
    approveLpo: builder.mutation({
      query: (recordGuid) => ({ url: `/stock/lpos/${recordGuid}/approve`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "StockLpo", id: recordGuid },
        { type: "StockLpo", id: "LIST" },
      ],
    }),
    rejectLpo: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/stock/lpos/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockLpo", id: recordGuid },
        { type: "StockLpo", id: "LIST" },
      ],
    }),
    cancelLpo: builder.mutation({
      query: ({ recordGuid, cancelReason }) => ({
        url: `/stock/lpos/${recordGuid}/cancel`,
        method: "POST",
        data: { cancelReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockLpo", id: recordGuid },
        { type: "StockLpo", id: "LIST" },
      ],
    }),
    // The PDF is built and captured entirely client-side (Supplier Copy
    // only, html2canvas+jsPDF from the same print template) - this call
    // just relays those bytes to the branch's own SMTP settings, no
    // server-side PDF generation involved.
    emailLpo: builder.mutation({
      query: ({ recordGuid, toAddress, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("toAddress", toAddress);
        return { url: `/stock/lpos/${recordGuid}/email`, method: "POST", data: formData };
      },
    }),

    // Multiple supporting documents/images per LPO - see Batch110_LpoAttachments.sql.
    // Same recipe as Petty Cash's receipt upload: multipart upload, blob-fetch on
    // explicit view/download, invalidates the LPO's own cache entry so
    // LpoDetail.jsx's attachment list refreshes automatically.
    // Batch199: kind = GENERAL (default) | INVOICE | CHEQUE, with the document's number and
    // date. An invoice or cheque changes the LPO list's Invoice / Payment columns too.
    uploadLpoAttachment: builder.mutation({
      query: ({ recordGuid, file, kind, referenceNo, referenceDate }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (kind) formData.append("kind", kind);
        if (referenceNo) formData.append("referenceNo", referenceNo);
        if (referenceDate) formData.append("referenceDate", referenceDate);
        return { url: `/stock/lpos/${recordGuid}/attachments`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "StockLpo", id: recordGuid }, { type: "StockLpo", id: "LIST" }],
    }),
    getLpoAttachmentContent: builder.query({
      query: ({ recordGuid, attachmentRecordGuid }) => ({
        url: `/stock/lpos/${recordGuid}/attachments/${attachmentRecordGuid}/content`,
        responseType: "blob",
      }),
    }),
    deleteLpoAttachment: builder.mutation({
      query: ({ recordGuid, attachmentRecordGuid }) => ({
        url: `/stock/lpos/${recordGuid}/attachments/${attachmentRecordGuid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "StockLpo", id: recordGuid }, { type: "StockLpo", id: "LIST" }],
    }),

    // ---- Stock-In (GRN) ----
    listStockIns: builder.query({
      query: (params) => ({ url: "/stock/stock-in", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((s) => ({ type: "StockIn", id: s.recordGuid })),
              { type: "StockIn", id: "LIST" },
            ]
          : [{ type: "StockIn", id: "LIST" }],
    }),
    getStockIn: builder.query({
      query: (recordGuid) => ({ url: `/stock/stock-in/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "StockIn", id: recordGuid }],
    }),
    createStockIn: builder.mutation({
      query: (body) => ({ url: "/stock/stock-in", method: "POST", data: body }),
      invalidatesTags: (result) => [
        { type: "StockIn", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
        ...(result?.lpoId ? [{ type: "StockLpo", id: "LIST" }] : []),
      ],
    }),
    // Soft-delete/void (Batch44) - reverses the stock-balance impact server-side.
    deleteStockIn: builder.mutation({
      query: ({ recordGuid, deleteReason }) => ({
        url: `/stock/stock-in/${recordGuid}/delete`,
        method: "POST",
        data: { deleteReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockIn", id: recordGuid },
        { type: "StockIn", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),

    // ---- Stock-Out ----
    listStockOuts: builder.query({
      query: (params) => ({ url: "/stock/stock-out", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((s) => ({ type: "StockOut", id: s.recordGuid })),
              { type: "StockOut", id: "LIST" },
            ]
          : [{ type: "StockOut", id: "LIST" }],
    }),
    getStockOut: builder.query({
      query: (recordGuid) => ({ url: `/stock/stock-out/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "StockOut", id: recordGuid }],
    }),
    createStockOut: builder.mutation({
      query: (body) => ({ url: "/stock/stock-out", method: "POST", data: body }),
      invalidatesTags: [
        { type: "StockOut", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),
    deleteStockOut: builder.mutation({
      query: ({ recordGuid, deleteReason }) => ({
        url: `/stock/stock-out/${recordGuid}/delete`,
        method: "POST",
        data: { deleteReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockOut", id: recordGuid },
        { type: "StockOut", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),

    // ---- Stock Return ----
    listStockReturns: builder.query({
      query: (params) => ({ url: "/stock/returns", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((r) => ({ type: "StockReturn", id: r.recordGuid })),
              { type: "StockReturn", id: "LIST" },
            ]
          : [{ type: "StockReturn", id: "LIST" }],
    }),
    getStockReturn: builder.query({
      query: (recordGuid) => ({ url: `/stock/returns/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "StockReturn", id: recordGuid }],
    }),
    createStockReturn: builder.mutation({
      query: (body) => ({ url: "/stock/returns", method: "POST", data: body }),
      invalidatesTags: [
        { type: "StockReturn", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),
    deleteStockReturn: builder.mutation({
      query: ({ recordGuid, deleteReason }) => ({
        url: `/stock/returns/${recordGuid}/delete`,
        method: "POST",
        data: { deleteReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockReturn", id: recordGuid },
        { type: "StockReturn", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),

    // ---- Stock Adjustment ----
    listStockAdjustments: builder.query({
      query: (params) => ({ url: "/stock/adjustments", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((a) => ({ type: "StockAdjustment", id: a.recordGuid })),
              { type: "StockAdjustment", id: "LIST" },
            ]
          : [{ type: "StockAdjustment", id: "LIST" }],
    }),
    getStockAdjustment: builder.query({
      query: (recordGuid) => ({ url: `/stock/adjustments/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "StockAdjustment", id: recordGuid }],
    }),
    createStockAdjustment: builder.mutation({
      query: (body) => ({ url: "/stock/adjustments", method: "POST", data: body }),
      invalidatesTags: [{ type: "StockAdjustment", id: "LIST" }],
    }),
    approveStockAdjustment: builder.mutation({
      query: (recordGuid) => ({ url: `/stock/adjustments/${recordGuid}/approve`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "StockAdjustment", id: recordGuid },
        { type: "StockAdjustment", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),
    rejectStockAdjustment: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/stock/adjustments/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockAdjustment", id: recordGuid },
        { type: "StockAdjustment", id: "LIST" },
      ],
    }),
    deleteStockAdjustment: builder.mutation({
      query: ({ recordGuid, deleteReason }) => ({
        url: `/stock/adjustments/${recordGuid}/delete`,
        method: "POST",
        data: { deleteReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "StockAdjustment", id: recordGuid },
        { type: "StockAdjustment", id: "LIST" },
        { type: "StockBalance", id: "LIST" },
      ],
    }),

    // ---- Balances & barcode/QR ----
    listStockBalances: builder.query({
      query: (params) => ({ url: "/stock/balances", params }),
      providesTags: (result) =>
        result?.items
          ? [
              // Keyed by product (Batch192): never-moved products have no balanceId.
              ...result.items.map((b) => ({ type: "StockBalance", id: b.productRecordGuid })),
              { type: "StockBalance", id: "LIST" },
            ]
          : [{ type: "StockBalance", id: "LIST" }],
    }),
    // Returns raw image/png (like the Customer QR endpoint) - goes through
    // as a Blob via axiosBaseQuery's responseType passthrough, not JSON.
    getStockQrCode: builder.query({
      query: ({ branchId, productCode, customBarcodeValue }) => ({
        url: "/stock/qrcode",
        params: { branchId, productCode, customBarcodeValue },
        responseType: "blob",
      }),
    }),
    // Most recent (non-voided) Stock-In rate for a product - used as a
    // pricing reference on Stock-Out (issue price often mirrors what was
    // last paid). {unitRate: null, ...} if the product was never purchased,
    // never an error.
    getLastPurchasePrice: builder.query({
      query: ({ branchId, productCode }) => ({
        url: "/stock/last-purchase-price",
        params: { branchId, productCode },
      }),
    }),

    // Stock Movement Report (Batch66) - a flat, drillable ledger unifying
    // Stock-In/Stock-Out/Return/Adjustment lines, one row per line item.
    // Server supports dateFrom/dateTo/branchId/productGroupId/productCode/
    // movementType/supplierSearch/search/includeVoided directly - no
    // client-side filtering needed here (unlike the other Stock lists).
    listStockMovementReport: builder.query({
      query: (params) => ({ url: "/stock/movement-report", params }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useListLposQuery,
  useGetLpoQuery,
  useLazyGetLpoQuery,
  useGetLpoRevisionsQuery,
  useCreateLpoMutation,
  useUpdateLpoMutation,
  useApproveLpoMutation,
  useRejectLpoMutation,
  useCancelLpoMutation,
  useEmailLpoMutation,
  useUploadLpoAttachmentMutation,
  useLazyGetLpoAttachmentContentQuery,
  useDeleteLpoAttachmentMutation,
  useListStockInsQuery,
  useGetStockInQuery,
  useCreateStockInMutation,
  useDeleteStockInMutation,
  useListStockOutsQuery,
  useGetStockOutQuery,
  useCreateStockOutMutation,
  useDeleteStockOutMutation,
  useListStockReturnsQuery,
  useGetStockReturnQuery,
  useCreateStockReturnMutation,
  useDeleteStockReturnMutation,
  useListStockAdjustmentsQuery,
  useGetStockAdjustmentQuery,
  useCreateStockAdjustmentMutation,
  useApproveStockAdjustmentMutation,
  useRejectStockAdjustmentMutation,
  useDeleteStockAdjustmentMutation,
  useListStockBalancesQuery,
  useGetStockQrCodeQuery,
  useLazyGetStockQrCodeQuery,
  useLazyGetLastPurchasePriceQuery,
  useListStockMovementReportQuery,
} = stockApi;
